import React from "react";
import { Box, Button, Typography } from "@mui/material";
import EmptyStateIcon, { EmptyStateIconName } from "./EmptyStateIcons";
import { emptyStateAccents, EmptyStateAccent } from "./EmptyState.tokens";
import {
  actionRowSx,
  bodySx,
  EmptyStateSize,
  ghostActionSx,
  linkActionSx,
  primaryActionSx,
  smallActionSx,
  tileSx,
  titleSx,
  wellSx,
} from "./EmptyState.styles";

export interface EmptyStateAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  id?: string;
}

export interface EmptyStateProps {
  /**
   * Chosen by the *container*, not by the message:
   * `full` for a page's main table or a full-width card, `medium` for a
   * half-width card, `compact` for a widget (no icon, one text link).
   */
  size?: EmptyStateSize;
  /** Omitted on `compact`, which carries no icon by design. */
  icon?: EmptyStateIconName;
  accent?: EmptyStateAccent;
  /** States the absence in the module's own noun. No trailing period. */
  title: string;
  /**
   * One or two sentences: what lives here and how it gets here. Drop it rather
   * than padding it out — a body that says nothing costs a line and gives none.
   */
  body?: React.ReactNode;
  /**
   * Repeats the container's own primary action — same label, same target.
   * Read-only lists (audit trails, reports) take none at all, and a *filtered*
   * empty offers the way back instead of the first-run action.
   */
  primaryAction?: EmptyStateAction;
  /** A genuine alternative route, or absent. Never "Learn more". */
  secondaryAction?: EmptyStateAction;
  /**
   * Draws the container's border on the well itself. Opt-in, and only for the
   * handful of screens whose empty state *is* the page body — everywhere else
   * the block sits inside a card or table that already draws one, and a border
   * here would nest a box inside a box.
   */
  framed?: boolean;
  /** Extra chrome for the well — a screen's own radius or min-height. */
  sx?: any;
}

/**
 * The one empty state for every list, table and panel in the app
 * (`Empty State.dc.html`).
 *
 * It replaces the *rows* only: the container's header and toolbar stay exactly
 * where they are, and the block paints the design's recessed well inside the
 * border they already draw.
 *
 * It is deliberately **not** a failure state. A fetch that errors uses the
 * disruption pattern with a Retry, never this block — an empty state tells the
 * user there is nothing here, which is a lie when the request simply failed.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  size = "full",
  icon = "doc",
  accent = "brand",
  title,
  body,
  primaryAction,
  secondaryAction,
  framed = false,
  sx,
}) => {
  const isCompact = size === "compact";
  const tileSize = size === "medium" ? "medium" : "full";
  const stroke = emptyStateAccents[accent].stroke;

  const renderActions = () => {
    if (!primaryAction && !secondaryAction) return null;

    // A widget has room for one text link, and only for the primary route.
    if (isCompact) {
      const action = primaryAction ?? secondaryAction!;
      return (
        <Button
          id={action.id}
          disableRipple
          onClick={action.onClick}
          disabled={action.disabled}
          sx={linkActionSx}
        >
          {action.label}
        </Button>
      );
    }

    // A half-width card takes one small ghost button, not a pair.
    if (size === "medium") {
      const action = primaryAction ?? secondaryAction!;
      return (
        <Button
          id={action.id}
          onClick={action.onClick}
          disabled={action.disabled}
          sx={smallActionSx}
        >
          {action.label}
        </Button>
      );
    }

    return (
      <Box sx={actionRowSx}>
        {secondaryAction && (
          <Button
            id={secondaryAction.id}
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            sx={ghostActionSx}
          >
            {secondaryAction.label}
          </Button>
        )}
        {primaryAction && (
          <Button
            id={primaryAction.id}
            variant="contained"
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            sx={primaryActionSx}
          >
            {primaryAction.label}
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={[wellSx(size, framed), ...(Array.isArray(sx) ? sx : [sx])]}>
      {!isCompact && (
        <Box sx={tileSx(tileSize, accent)}>
          <EmptyStateIcon
            name={icon}
            stroke={stroke}
            size={tileSize === "medium" ? 17 : 21}
          />
        </Box>
      )}
      <Typography sx={titleSx(size)}>{title}</Typography>
      {!isCompact && body && (
        <Typography component="div" sx={bodySx(tileSize)}>
          {body}
        </Typography>
      )}
      {renderActions()}
    </Box>
  );
};

export default EmptyState;
